import { useEffect, useState } from 'react';

import { FormControlLabel } from '@mui/material';
import { ApiClientsAccessSelectInput } from 'pages/ApiClients/components/ApiClientsAccessSelectInput/ApiClientsAccessSelectInput';
import { NewSchemeInfoTooltip } from 'pages/ApiClients/components/NewSchemeInfoTooltip';
import { AccessRole } from 'pages/ApiClients/constants/accessSelectItems';
import { FormDataConsumer, required } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { Alert } from 'shared/mui/Alert/Alert';
import { Box } from 'shared/mui/Box';
import { Switch } from 'shared/mui/Switch';
import {
  AutocompleteInput,
  AutocompleteMultiselectInput,
  BooleanInput,
  TextInput,
} from 'shared/react-admin/Inputs';
import { TextInputWithAlias } from 'shared/react-admin/Inputs/TextInputs';
import { ReferenceArrayInput, ReferenceInput } from 'shared/react-admin/ReferenceInputs';

export type ApiClientCreateFormValues = {
  active: boolean;
  name: string;
  alias: string;
  roles: AccessRole[];
  merchant?: string | null;
  merchants?: string[];
};

export const ApiClientsCreateForm = () => {
  const [isNewAuthScheme, setIsNewAuthScheme] = useState(false);
  const { watch, unregister } = useFormContext<ApiClientCreateFormValues>();
  const roles = watch('roles');

  const isShowNewAuthScheme = roles?.includes(AccessRole.Merchant);

  useEffect(() => {
    if (!isShowNewAuthScheme) {
      unregister(['merchant', 'merchants']);
      return;
    }

    unregister(isNewAuthScheme ? 'merchant' : 'merchants');
  }, [isNewAuthScheme, roles]);

  const handleChange = () => setIsNewAuthScheme((prev) => !prev);

  return (
    <Box py="24px" width="100%">
      <BooleanInput
        defaultValue={true}
        label="Активен"
        size="small"
        source="active"
        sx={{ marginBottom: '24px' }}
      />
      <Box display="flex" gap="24px">
        <TextInput
          fullWidth
          label="Название"
          size="small"
          slotProps={{
            inputLabel: { shrink: true },
          }}
          source="name"
          validate={required()}
          variant="outlined"
        />
        <TextInputWithAlias
          fullWidth
          label="Алиас"
          size="small"
          slotProps={{
            inputLabel: { shrink: true },
          }}
          source="alias"
          validate={required()}
          variant="outlined"
        />
        <ApiClientsAccessSelectInput />
      </Box>
      {isShowNewAuthScheme && (
        <>
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={isNewAuthScheme}
                  name="scheme"
                  onChange={handleChange}
                  size="small"
                />
              }
              label="Новая схема аутентификации"
              sx={{ marginRight: 0, marginLeft: 0 }}
            />
            <NewSchemeInfoTooltip />
          </Box>
          {isNewAuthScheme ? (
            <>
              <Alert severity="warning" sx={{ marginTop: 3 }}>
                После создания клиента API отредактировать схему аутентификации будет невозможно.
              </Alert>
              <ReferenceArrayInput reference="merchants" source="merchants">
                <FormDataConsumer<ApiClientCreateFormValues>>
                  {({ formData }) => {
                    const disabled = (formData.roles || []).includes(AccessRole.Service);

                    return (
                      <AutocompleteMultiselectInput
                        disabled={disabled}
                        filterToQuery={(searchText: string) => ({ name: searchText })}
                        fullWidth
                        label="Мерчанты"
                        name="merchants"
                        optionText="name"
                        size="small"
                        sx={{ marginTop: '24px' }}
                        validate={required()}
                      />
                    );
                  }}
                </FormDataConsumer>
              </ReferenceArrayInput>
            </>
          ) : (
            <ReferenceInput reference="merchants" source="merchant">
              <AutocompleteInput
                filterToQuery={(searchText: string) => ({ name: searchText })}
                fullWidth
                label="Мерчант"
                optionText="name"
                size="small"
                sx={{ marginTop: '24px' }}
                validate={required()}
                variant="outlined"
              />
            </ReferenceInput>
          )}
        </>
      )}
    </Box>
  );
};
